/**
 * /checkin/history — "My Check-Ins": read-only list of the user's
 * saved conversations. Purely browse + delete + rename here — no
 * paid gate on OPENING a thread; that's decided inside
 * CheckInConversation.jsx via the `send_enabled` flag returned by
 * /conversation/{id}.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckInShell, CheckInLogo, CHECKIN_PALETTE } from "./_shell";
import { checkinApi, getToken, clearAuth } from "@/lib/checkinApi";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
}

export default function CheckInHistory() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const out = await checkinApi.listConversations();
      setItems(out.items || []);
    } catch (e) {
      if (e.status === 401) { clearAuth(); navigate("/checkin/signin"); return; }
      setErr(e.message || "Couldn't load your check-ins.");
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/checkin", { replace: true }); return; }
    load();
  }, [load, navigate]);

  async function onDelete(id) {
    if (!window.confirm(
      "Delete this check-in? The full thread will be removed from your history. This can't be undone."
    )) return;
    try {
      await checkinApi.deleteConversation(id);
      await load();
    } catch (e) { alert(e.message || "Couldn't delete."); }
  }

  async function onRename(id, current) {
    const title = window.prompt("Rename this check-in:", current || "Check-In");
    if (!title || title.trim().length < 1) return;
    try {
      await checkinApi.renameConversation(id, title.trim());
      await load();
    } catch (e) { alert(e.message || "Couldn't rename."); }
  }

  return (
    <CheckInShell testid="checkin-history">
      <div className="pt-4">
        <CheckInLogo />
      </div>
      <div className="mt-8">
        <button
          onClick={() => navigate("/checkin/home")}
          className="text-[12px] underline"
          style={{ color: CHECKIN_PALETTE.purple }}
          data-testid="history-back-home"
        >
          ← Home
        </button>
        <h1
          className="mt-2 font-serif tracking-tight text-3xl"
          style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          data-testid="history-title"
        >
          My Check-Ins
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "#5C5468" }}>
          Every check-in is saved here so you can come back and reread
          anytime — even when you&rsquo;re between Talk Credit packs or
          memberships.
        </p>
      </div>

      {err && (
        <div className="mt-4 rounded-lg border p-3 text-sm"
             style={{ borderColor: "#B4223C", background: "#FCEFF1", color: "#B4223C" }}
             data-testid="history-error">
          {err}
        </div>
      )}

      <div className="mt-6 flex-1">
        {items === null ? (
          <div className="text-sm" style={{ color: "#6C6478" }} data-testid="history-loading">
            Loading your check-ins…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-6 text-center"
               style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}
               data-testid="history-empty">
            <div className="text-[13px]" style={{ color: "#5C5468" }}>
              You haven&rsquo;t started a check-in yet.
            </div>
            <button
              onClick={() => navigate("/checkin/conversation")}
              className="mt-3 rounded-full px-5 py-2 text-[13px] font-medium text-white"
              style={{ background: CHECKIN_PALETTE.purple }}
              data-testid="history-empty-cta"
            >
              Begin your check-in
            </button>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="history-list">
            {items.map((it) => (
              <li key={it.id}>
                <div
                  className="rounded-2xl border p-4 bg-white flex flex-col gap-2"
                  style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
                  data-testid={`history-item-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => navigate(`/checkin/conversation?id=${encodeURIComponent(it.id)}`)}
                      className="text-left flex-1"
                      data-testid={`history-open-${it.id}`}
                    >
                      <div className="text-[14px] font-medium" style={{ color: CHECKIN_PALETTE.ink }}>
                        {it.title || "Check-In"}
                      </div>
                      <div className="mt-1 text-[11px]" style={{ color: "#6C6478" }}>
                        {fmtDate(it.updated_at || it.created_at)} · {it.message_count} messages
                      </div>
                    </button>
                    <StatusPill status={it.display_status} />
                  </div>
                  <div className="flex gap-4 text-[11px]" style={{ color: "#6C6478" }}>
                    <button onClick={() => onRename(it.id, it.title)}
                            className="underline" data-testid={`history-rename-${it.id}`}>
                      Rename
                    </button>
                    <button onClick={() => onDelete(it.id)}
                            className="underline"
                            style={{ color: "#B4223C" }}
                            data-testid={`history-delete-${it.id}`}>
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </CheckInShell>
  );
}

function StatusPill({ status }) {
  const color = /Paused/.test(status) ? "#B4223C"
              : /Completed/.test(status) ? "#6C6478"
              : /Complimentary/.test(status) ? CHECKIN_PALETTE.purple
              : "#3F8F6A";
  const bg = /Paused/.test(status) ? "rgba(180,34,60,0.08)"
           : /Completed/.test(status) ? "rgba(108,100,120,0.08)"
           : /Complimentary/.test(status) ? "rgba(92,30,138,0.08)"
           : "rgba(63,143,106,0.08)";
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap"
      style={{ background: bg, color }}
      data-testid="history-status-pill"
    >
      {status}
    </span>
  );
}
